//@ts-nocheck
import globalConfig from '../../../config/globalConfig';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { XGButton } from 'xg-cl';
import { UploadSimple, Trash } from '@phosphor-icons/react';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorToast } from '../../../utils/alerts';
import {
  getPresignedUrl,
  uploadToBucket
} from '../../../services/upload/upload';
import {
  SUPPORTED_IMAGE_EXTENSIONS,
  isSupportedImageFile,
  getSupportedFormatsString
} from '../../../utils/supportedImages';
import { bytesToMiB } from '../../../utils/fileHelper';
import * as PropTypes from 'prop-types';
import './ImagePicker.css';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  STOCK_PHOTO_MAX_IMAGE_SIZE,
  pathReference as imagePaths
} from '../../../constants/files';
import { useSelector } from 'react-redux';
import PhotoPickerModal from '../PhotoPicker/PhotoPickerModal';
import { importStockPhoto } from '../../../services/photo/photo';

const DEFAULT_MAX_IMAGE_SIZE = 20 * 1024 * 1024;

/**
 * Functional React component for rendering a custom image picker section.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties.
 * @param {string} [props.callback] - The callback function to be called when an image is selected.
 * @param {string} [props.id] - The ID for the login header element.
 * @param {string} [props.title] - The title for the image picker.
 * @param {string} [props.url] - The URL for the image picker.
 * @param {string} [props.fileType] - File type to process, it can be "image" or "file".
 * @param {Function} [props.onUploadStart] - Callback when upload starts.
 * @param {Function} [props.onUploadComplete] - Callback when upload completes successfully.
 * @param {Function} [props.onUploadError] - Callback when upload fails.
 * @param {Object} [props.rest] - Additional props to be spread onto the image picker element.
 *
 * @returns {JSX.Element} React element representing the image picker.
 */

const ImagePicker = ({
  callback,
  handleUploading = () => {},
  handleDelete = () => {},
  id,
  title,
  url,
  fileType,
  pathReference,
  tripId,
  useVersion = false,
  showDelete = false,
  disabledCTA = false,
  accept = SUPPORTED_IMAGE_EXTENSIONS,
  deleteLabel = '',
  uploadFile = null,
  validateFile = null,
  showUploadErrorToast = true,
  onUploadStart = () => {},
  onUploadComplete = () => {},
  onUploadError = () => {},
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common']);
  const { enableStockPhotoPicker = false } = useFlags();
  const inputRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(url);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [localBlob, setLocalBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const trip = useSelector((state) => state.trip);
  const tripImageSurface =
    !!tripId &&
    [
      imagePaths.TRIP,
      imagePaths.DAY_ITENERARY,
      imagePaths.CONTENT,
      imagePaths.DETAILED_DAY
    ].includes(pathReference);
  const stockEnabled =
    enableStockPhotoPicker &&
    (tripImageSurface || pathReference === imagePaths.LIBRARY);
  const placeHolder = '/img/trip-placeholder-image.png';
  const filetype = fileType;

  const validateSelectedFile = (file) => {
    if (typeof validateFile === 'function') {
      return validateFile(file);
    }

    if (
      !file ||
      !file.type.startsWith('image/') ||
      !isSupportedImageFile(file)
    ) {
      const formats = getSupportedFormatsString();
      return t('common:imageFormatNotSupported', { formats });
    }

    const maxImageSize = enableStockPhotoPicker
      ? STOCK_PHOTO_MAX_IMAGE_SIZE
      : Number(globalConfig.maxImageSize) || DEFAULT_MAX_IMAGE_SIZE;

    if (file.size > maxImageSize) {
      return t('common:imageFileTooBig', {
        maxImageSize: bytesToMiB(maxImageSize)
      });
    }

    return '';
  };

  const handleChange = async (event) => {
    const file = event.target.files[0];
    const validationError = validateSelectedFile(file);

    if (validationError) {
      ErrorToast(validationError);
      // Reset input value to prevent keeping unsupported file
      event.target.value = '';
      return false;
    }

    const previousImageUrl = imageUrl;
    const localImageUrl = URL.createObjectURL(file);

    //Set local blob in case of error
    setLocalBlob(localImageUrl);

    //Set image url before upload to prevent flickering
    setImageUrl(localImageUrl);

    try {
      setIsLoading(true);
      updateUploading(true);
      onUploadStart();

      if (typeof uploadFile === 'function') {
        const nextImageUrl = await uploadFile(file);

        if (typeof nextImageUrl === 'string') {
          setImageUrl(nextImageUrl);
        }

        setShowPlaceholder(false);
        onUploadComplete();
        setPickerOpen(false);
        return true;
      }

      const presignedImage = await getPresignedImage({
        fileType: filetype,
        files: [
          {
            name: file.name,
            size: file.size,
            type: file.type,
            pathReference,
            tripId
          }
        ]
      });

      if (presignedImage) {
        const uploadedImage = await uploadImageToBucket(presignedImage, file);

        // request.put returns data directly via interceptor; axios.put returns full response
        const status = uploadedImage?.status || 200; // request returns data, not response; assume 200 on success path
        if (status === 200) {
          // Use BE-provided publicUrl when available (imageUploadBE flag enabled),
          // otherwise use presignedUrl without query params (legacy S3 flow)
          const computedUrl =
            presignedImage?.data?.publicUrl ||
            presignedImage.data.presignedUrl.split('?')[0];

          const saveResult = await callback(computedUrl);
          if (
            saveResult === false ||
            saveResult?.data === false ||
            saveResult?.error
          ) {
            throw new Error('Image could not be saved');
          }
          setImageUrl(computedUrl);
          setShowPlaceholder(false);
          onUploadComplete();
          setPickerOpen(false);
        } else {
          throw new Error('Error uploading image');
        }
      } else {
        throw new Error('Error uploading image');
      }
    } catch (err) {
      setImageUrl(previousImageUrl || placeHolder);

      if (showUploadErrorToast) {
        ErrorToast(t('common:imageUploadFailed'));
      }

      onUploadError();
      return false;
    } finally {
      updateUploading(false);
      setIsLoading(false);
      event.target.value = '';
    }
    return true;
  };

  const handleStockConfirm = async ([photo]) => {
    setIsLoading(true);
    updateUploading(true);
    onUploadStart();
    try {
      const data = await importStockPhoto({
        provider: photo.provider,
        providerPhotoId: photo.providerPhotoId,
        pathReference,
        tripId
      });
      if (!data?.publicUrl) throw new Error('Missing imported photo URL');
      const saveResult = await callback(data.publicUrl);
      if (
        saveResult === false ||
        saveResult?.data === false ||
        saveResult?.error
      ) {
        throw new Error('Image could not be saved');
      }
      setImageUrl(data.publicUrl);
      setShowPlaceholder(false);
      onUploadComplete();
      setPickerOpen(false);
    } catch (error) {
      onUploadError();
      throw error;
    } finally {
      updateUploading(false);
      setIsLoading(false);
    }
  };

  const handleCoverDrop = (event) => {
    event.preventDefault();
    if (!stockEnabled && !isLoading && !disabledCTA) {
      const file = event.dataTransfer?.files?.[0];
      if (file) handleChange({ target: { files: [file], value: '' } });
    }
  };

  const updateUploading = (value = false) => {
    if (typeof handleUploading === 'function') {
      handleUploading(value);
    }
  };

  const getPresignedImage = async (fileType, images = []) => {
    const { data } = await getPresignedUrl(fileType, images);

    const presignedImage = data && data.length ? data[0] : null;

    return presignedImage;
  };

  const uploadImageToBucket = async (presignedImage, file) => {
    const result = await uploadToBucket(presignedImage, file);

    return result;
  };

  //Sync image url from url prop to imageUrl state
  useEffect(() => {
    if (url) {
      setImageUrl(url);
      setShowPlaceholder(false);
    } else {
      setImageUrl(placeHolder);
      setShowPlaceholder(true);
    }
  }, [url]);

  const pickerUrl = useVersion
    ? `${imageUrl && imageUrl?.indexOf('blob') === -1 ? imageUrl + '?v=' + new Date().getTime() : ''}`
    : imageUrl;

  return (
    <Box
      {...rest}
      className={rest.className}
      data-testid='image-picker'
      id={id}
      onDragOver={
        tripImageSurface ? (event) => event.preventDefault() : undefined
      }
      onDrop={tripImageSurface ? handleCoverDrop : undefined}
    >
      <Box className='image-picker__cover'>
        <img
          src={pickerUrl}
          key={useVersion ? new Date().getTime() : null}
          alt=''
          draggable='false'
          onError={(evt) => {
            // Set local blob or placeholder if error
            evt.target.src = localBlob ? localBlob : placeHolder;
          }}
        />

        <Box
          className='image-picker__content'
          style={{
            justifyContent: `${title ? 'space-between' : 'end'}`
          }}
        >
          {title && (
            <h2
              className={`h2-semibold ${
                imageUrl ? 'image-picker__content__title' : ''
              } `}
            >
              {title}
            </h2>
          )}

          <Box className='image-picker__button'>
            <Box className='relative-position'>
              <XGButton
                className='image-picker__button__upload'
                disabled={isLoading || disabledCTA}
                startIcon={<UploadSimple width={16} color='#344054' />}
                id={`image-picker-upload-file`}
                text={!showPlaceholder ? t('changeCover') : t('addCover')}
                type='secondary'
                size='medium'
                variant='outlined'
                onClick={() => {
                  if (stockEnabled) setPickerOpen(true);
                  else inputRef.current.click();
                }}
              />
              {isLoading && (
                <CircularProgress
                  size={22}
                  className='spinner spinner__secondary'
                />
              )}
            </Box>
            {showDelete && !showPlaceholder && (
              <Box className='image-picker__button__remove'>
                <span>
                  <Tooltip className='image-picker__button__upload'>
                    <span>
                      <IconButton
                        onClick={handleDelete}
                        aria-label={deleteLabel || t('common:delete')}
                        className='additional-content-item__delete'
                        size='small'
                        disabled={
                          isLoading ||
                          disabledCTA ||
                          !imageUrl ||
                          imageUrl === placeHolder
                        }
                      >
                        <Trash size={20} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </span>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <input
        type='file'
        ref={inputRef}
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {stockEnabled && (
        <PhotoPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handleStockConfirm}
          destinationTerm={
            tripImageSurface
              ? trip?.city?.cityname || trip?.country?.countryname || ''
              : ''
          }
          tripTitle={tripImageSurface ? trip?.tripname || '' : ''}
          onUploadConfirm={async ([file]) =>
            handleChange({ target: { files: [file], value: '' } })
          }
        />
      )}
    </Box>
  );
};

/**
 * Prop type definitions for ImagePicker.
 */
ImagePicker.propTypes = {
  callback: PropTypes.func.isRequired,
  className: PropTypes.string,
  handleUploading: PropTypes.func,
  handleDelete: PropTypes.func,
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  url: PropTypes.string,
  fileType: PropTypes.string,
  pathReference: PropTypes.string,
  tripId: PropTypes.number,
  useVersion: PropTypes.bool,
  showDelete: PropTypes.bool,
  disabledCTA: PropTypes.bool,
  accept: PropTypes.string,
  deleteLabel: PropTypes.string,
  uploadFile: PropTypes.func,
  validateFile: PropTypes.func,
  showUploadErrorToast: PropTypes.bool,
  onUploadStart: PropTypes.func,
  onUploadComplete: PropTypes.func,
  onUploadError: PropTypes.func
};

export default ImagePicker;
